import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { execAPI } from '../services/exec'
import type {
  Dashboard,
  Goal,
  MeasuresRegistry,
  MeasureDefinition,
  DimensionDefinition,
} from '../types/exec'
import type { DashboardFilters } from '../types/govdata'

/** Which preset set the WidgetPicker should surface for this dashboard.
 *  - 'leadership' (default): standard /exec/dashboard presets — speed, competition,
 *    small-business, vehicle mix, vendor concentration, color-of-money, etc.
 *  - 'dib': geo-focused presets used by /exec/dib/dashboard — keeps state-government
 *    / vendor-side widgets out of the leadership picker (and vice versa).
 */
export type PresetScope = 'leadership' | 'dib'

interface ExecDashboardContextType {
  /** The currently loaded dashboard (with widgets) */
  dashboard: Dashboard | null
  /** Loading state for the dashboard */
  loading: boolean
  /** Error message if dashboard load failed */
  error: string | null
  /** Dashboard-level filters inherited by all widgets */
  filters: DashboardFilters
  /** Update dashboard-level filters */
  setFilters: (filters: DashboardFilters) => void
  /** Active goals for the current office scope */
  goals: Goal[]
  /** Measures registry */
  measures: Record<string, MeasureDefinition>
  /** Dimensions registry */
  dimensions: Record<string, DimensionDefinition>
  /** Refresh the dashboard from the API */
  refetchDashboard: () => Promise<void>
  /** Refresh goals from the API */
  refetchGoals: () => Promise<void>
  /** Which preset set the WidgetPicker should surface */
  presetScope: PresetScope
  /**
   * ECharts group key used to synchronize hover crosshair + dataZoom across
   * widgets in this dashboard (Phase F1). Renderers set `chart.group =
   * chartGroup` on ChartReady; ExecDashboard calls `echarts.connect(chartGroup)`
   * after mount. Empty string when no dashboard is loaded.
   */
  chartGroup: string
}

const ExecDashboardContext = createContext<ExecDashboardContextType | undefined>(undefined)

interface ExecDashboardProviderProps {
  dashboardId: string | null
  children: ReactNode
  /** Which preset set the WidgetPicker should surface. Defaults to 'leadership'. */
  presetScope?: PresetScope
}

export const ExecDashboardProvider: React.FC<ExecDashboardProviderProps> = ({
  dashboardId,
  children,
  presetScope = 'leadership',
}) => {
  const [dashboard, setDashboard] = useState<Dashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Default to the last 3 *complete* fiscal years. The current FY is still in
  // progress, so its data is partial — including it makes YoY deltas (e.g. the
  // Attention widget) read as cliffs (-100% obligated). Users can still extend
  // the range to the in-progress FY via the slider.
  const [filters, setFilters] = useState<DashboardFilters>(() => {
    const now = new Date()
    const currentFY = now.getMonth() >= 9 ? now.getFullYear() + 1 : now.getFullYear()
    const lastCompleteFY = currentFY - 1
    const years = Array.from({ length: 3 }, (_, i) => lastCompleteFY - 2 + i)
    return { fiscal_year: years.join(',') }
  })
  const [goals, setGoals] = useState<Goal[]>([])
  const [measures, setMeasures] = useState<Record<string, MeasureDefinition>>({})
  const [dimensions, setDimensions] = useState<Record<string, DimensionDefinition>>({})

  // Load measures registry (once)
  useEffect(() => {
    execAPI.getMeasuresRegistry().then((registry: MeasuresRegistry) => {
      setMeasures(registry.measures)
      setDimensions(registry.dimensions)
    }).catch(console.error)
  }, [])

  // Load dashboard
  const refetchDashboard = useCallback(async () => {
    if (!dashboardId) return
    setLoading(true)
    setError(null)
    try {
      const data = await execAPI.getDashboard(dashboardId)
      setDashboard(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard')
    } finally {
      setLoading(false)
    }
  }, [dashboardId])

  useEffect(() => {
    refetchDashboard()
  }, [refetchDashboard])

  // Load goals for the dashboard's office scope
  const refetchGoals = useCallback(async () => {
    try {
      const officeId = dashboard?.office ?? undefined
      const data = await execAPI.getGoals(officeId ? { office: officeId } : undefined)
      setGoals(data)
    } catch (err) {
      console.error('Failed to load goals:', err)
    }
  }, [dashboard?.office])

  useEffect(() => {
    if (dashboard) {
      refetchGoals()
    }
  }, [dashboard?.office, refetchGoals])

  return (
    <ExecDashboardContext.Provider
      value={{
        dashboard,
        loading,
        error,
        filters,
        setFilters,
        goals,
        measures,
        dimensions,
        refetchDashboard,
        refetchGoals,
        presetScope,
        chartGroup: dashboard?.id ? `dashboard-${dashboard.id}` : '',
      }}
    >
      {children}
    </ExecDashboardContext.Provider>
  )
}

export function useExecDashboard() {
  const ctx = useContext(ExecDashboardContext)
  if (!ctx) {
    throw new Error('useExecDashboard must be used within an ExecDashboardProvider')
  }
  return ctx
}
